import { debounce, get, orderBy, uniqBy } from 'lodash'
import { castToSnapshot, getSnapshot, Instance, SnapshotOut, types } from 'mobx-state-tree'
import { Keyboard } from 'react-native'

import { GAME_CONFIG, GameModeTag, Snapshot, TableEventKind, THREE_CARD_AMOUNT_OF_HANDS } from '@ppwr/poker'

import { SNACK_TYPES } from '../../navigation/toast'
import { extendLogger, LoggerContexts } from '../../utils/logger'
import { events } from '../analytics/analytics-events'
import { MessageContractModel } from '../api-store'
import { withRootStore } from '../extensions'
import { APIEvents } from '../messages/messages'
import { GameModal, GameModalKinds, LayerType } from './game-modals'
import { playSound } from '../../utils/audioService'
import { router } from 'expo-router'

const logger = extendLogger(LoggerContexts.GameEvents)

export enum GameEventTypes {
  processed = 'processed'
}

export const GameEvent = types.model('GameEvent').props({
  message: MessageContractModel
})
export interface GameEventType extends Instance<typeof GameEvent> {}
export interface GameEventSnapshot extends SnapshotOut<typeof GameEvent> {}

const DO_NOT_MODIFY_SNAPSHOT_EVENTS = [
  TableEventKind.CalculateNextBlindEvent,
  TableEventKind.Finished,
  TableEventKind.TimerStarted
] as const

export const GameEventQueue = types
  .model('GameEventQueue')
  .props({
    processedEventQueue: types.optional(types.array(GameEvent), []),
    eventQueue: types.optional(types.array(GameEvent), []),
    incomingEventQueue: types.optional(types.array(GameEvent), []),
    timeout: types.maybe(types.frozen<NodeJS.Timeout>()),
    timeoutPlayerAction: types.maybe(types.frozen<NodeJS.Timeout>()),
    lastEventVersion: types.optional(types.number, -1)
  })
  .extend(withRootStore)
  .volatile((self) => {
    const unsubscribeOnGameDelete = debounce(() => {
      self.rootStore.game.createGameDeletedAlert()
      self.rootStore.communication.endCall()
      return
    }, 1000)
    const attemptToProcess = clearTimeout(undefined)
    return { unsubscribeOnGameDelete, attemptToProcess }
  })
  .volatile((self) => {
    const unsubscribeOnRemovedByTeacher = debounce(() => {
      self.rootStore.game.createRemovedByTeacherModal()
      return
    }, 1000)
    const attemptToProcess = clearTimeout(undefined)
    return { unsubscribeOnRemovedByTeacher, attemptToProcess }
  })
  .views((self) => ({
    getFirstGameEvent(): GameEventType | undefined {
      if (self.eventQueue.length) {
        return self.eventQueue[0]
      }
      return undefined
    }
  }))
  .actions((self) => ({
    clearLastEventVersion() {
      self.lastEventVersion = -1
    },
    setLastEventVersion(value: number) {
      self.lastEventVersion = value
    },
    clearTimeout() {
      if (self.timeout) {
        clearTimeout(self.timeout)
      }
      self.timeout = undefined
    },
    setTimeout(timeout: NodeJS.Timeout | undefined) {
      self.timeout = timeout
    },
    setTimeoutPlayerAction(timeout: NodeJS.Timeout | undefined) {
      self.timeoutPlayerAction = timeout
    },
    clearTimeoutPlayerAction() {
      if (self.timeoutPlayerAction) {
        clearTimeout(self.timeoutPlayerAction)
      }
      self.timeoutPlayerAction = undefined
    }
  }))
  .actions((self) => ({
    clearGameEvents() {
      self.clearTimeout()
      self.processedEventQueue.clear()
      self.eventQueue.clear()
    },
    processEvent(): GameEventSnapshot | undefined {
      const getGameAttributeData = {
        gameId: self.rootStore.game.gameId,
        isTournamentModeOn: self.rootStore.game.isTournamentModeOn,
        isGameOver: self.rootStore.game.isGameOver,
        userId: self.rootStore.session.user?._id.toString() ?? '',
        channelName: self.rootStore.communication.channel?.channelName,
        communityCards: self.rootStore.game?.defaultTable?.communityCards ?? [],
        tableSnapshot: self.rootStore.game?.defaultTable,
        playerHoleCards: self.rootStore.game?.myHoleCards,
        playerActionsHints: self.rootStore.game?.myActions
      }
      const original = self.getFirstGameEvent()
      if (!original) {
        return undefined
      }

      const event = getSnapshot<GameEventSnapshot>(original)
      const userId = self.rootStore.session.user?._id.toString() ?? ''
      const message = event.message

      self.rootStore.game.setShouldHighlightCurrentPlayer(true)

      const kind = message.payload?.kind
      const isMe = get(event, 'message.payload.userId', '') === userId
      if (!DO_NOT_MODIFY_SNAPSHOT_EVENTS.includes(kind) && message.payload.snapshot) {
        self.rootStore.game.setGame(message.payload.snapshot, kind)
      }
      if (kind === APIEvents.GamePlayerJoined) {
        if (isMe) {
          self.rootStore.game
            .joinGame()
            .then(async (res) => {
              if (res?.redirectToGameOver && self.rootStore.game.isTournamentModeOn) {
                await self.rootStore.game.getTournamentStats()
              }
              self.rootStore.game.setShowPlayerTimerAfterJoinedGame(true)
            })
            .catch((error: unknown) => {
              logger.error(error)
            })
        }
      } else if ([TableEventKind.TimerStarted].includes(kind)) {
        const startTimerCheckpoint = message.payload.snapshot.table[0].startTimerCheckpoint
        const secondsPassed = GAME_CONFIG.START_TIMER_DELAY - Math.floor((Date.now() - startTimerCheckpoint) / 1000)

        if (secondsPassed > 0 && self.rootStore.game.gameKind !== GameModeTag.threeCard) {
          self.rootStore.messages.setSnack({
            type: SNACK_TYPES.GameStart,
            seconds: Math.min(GAME_CONFIG.START_TIMER_DELAY, secondsPassed),
            showSeconds: true,
            payload: undefined
          })
          self.rootStore.game.setTimerCountDown(Math.min(GAME_CONFIG.START_TIMER_DELAY, secondsPassed))
        }
      } else if ([TableEventKind.Reset, TableEventKind.Started].includes(kind)) {
        self.rootStore.game.setIsHandWinnersEvent(false)
        self.rootStore.communication.clearShowCards()
      } else if (kind === TableEventKind.HandWinners) {
        if (self.rootStore.session.selectedDailyTask) {
          self.rootStore.session.updateDailyTaskStatus()
        }
        self.rootStore.communication.clearCelebrations()
        self.rootStore.communication.clearShowCards()
        const snapshot = message.payload.snapshot as Snapshot
        const players = snapshot.players.filter((p) => p.chips)
        const isTournamentOver = players.length === 1 && self.rootStore.game.isTournamentModeOn
        // TODO: Clean this up. Maybe snapshot includes this
        if (isTournamentOver) {
          self.rootStore.game.setIsTournamentGameOver(true)
          requestAnimationFrame(async () => {
            await self.rootStore.game.getTournamentStats()
          })
        }
        Keyboard.dismiss()
        if (self.rootStore.game.gameKind !== GameModeTag.threeCard) {
          const shouldShowCardSheet =
            !(self.rootStore.game.isMyPlayerObserver || self.rootStore.game.isMyPlayerOut) &&
            self.rootStore.game.myHoleCards.length

          if (shouldShowCardSheet) {
            if (![GameModeTag.practiceTournament, GameModeTag.practice].includes(self.rootStore.game.gameKind)) {
              self.rootStore.game.createShowCardsSheet(GAME_CONFIG.DELAY_BETWEEN_HANDS + 1)
            }
          }
          self.rootStore.game.setIsHandWinnersEvent(true)
        }
      } else if ([TableEventKind.CalculateNextBlindEvent].includes(kind)) {
        const nextBlindIndex = message.payload.snapshot.table[0].nextBlindIndex
        const nextBlindValues = get(message.payload.snapshot, 'table[0].nextBlinds', { big: 0, small: 0 })
        const currentBlindValues = get(message.payload.snapshot, 'table[0].blinds', { big: 0, small: 0 })
        if (
          self.rootStore.game.currentBlind.big < currentBlindValues.big &&
          self.rootStore.game.currentBlind.small < currentBlindValues.small
        ) {
          self.rootStore.game.setCurrentBlinds(
            currentBlindValues.small === null ? { big: 0, small: 0 } : currentBlindValues
          )
        }
        if (
          self.rootStore.game.nextBlind.big < nextBlindValues.big &&
          self.rootStore.game.nextBlind.small < nextBlindValues.small
        ) {
          self.rootStore.game.setNextBlinds(nextBlindValues.small === null ? { big: 0, small: 0 } : nextBlindValues)
        }
        self.rootStore.game.setBlindLevel(nextBlindIndex)
        self.rootStore.game.startBlindTimer()
      } else if ([TableEventKind.PlayerAllIn].includes(kind)) {
        const allInPlayer = get(message.payload, 'snapshot.forPlayer', '')
        const isMe = allInPlayer.userId === self.rootStore.session.user?._id
        if (!isMe) {
          self.rootStore.messages.setSnack({
            type: SNACK_TYPES.AllIn,
            seconds: 5,
            showSeconds: false,
            payload: { type: SNACK_TYPES.AllIn, userName: allInPlayer.name, amount: allInPlayer.bet }
          })
        }
        if (self.rootStore.permissions.isSoundEnabled) {
          playSound('allIn')
        }
      } else if ([TableEventKind.PlayerBet].includes(kind)) {
        if (self.rootStore.permissions.isSoundEnabled) {
          playSound('bet')
        }
      } else if ([TableEventKind.Paused].includes(kind)) {
        self.rootStore.messages.clearSnack()
        self.rootStore.messages.setSnack({
          type: SNACK_TYPES.GamePaused,
          seconds: 5,
          showSeconds: false,
          payload: undefined
        })
      } else if ([TableEventKind.Resumed].includes(kind)) {
        self.rootStore.game.setHasUserTappedOnAction(false)
        self.rootStore.messages.clearSnack()
        setTimeout(() => {
          self.rootStore.messages.setSnack({
            type: SNACK_TYPES.GameResumed,
            seconds: 5,
            showSeconds: false,
            payload: undefined
          })
        }, 50)
      } else if ([APIEvents.AgoraUserUpdate].includes(kind)) {
        logger.info(`APIEvents.AgoraUserUpdate ${JSON.stringify(message.payload)}`)
        const playerAVCallStatus = get(message, 'payload.playersOnCall', [])
        self.rootStore.communication.setUsersOnVideoCall(playerAVCallStatus)
      } else if ([APIEvents.GameDeleted].includes(kind)) {
        // logger.info(`[game-events] ${APIEvents.GameDeleted}`)
        self.rootStore.game.setIsGameDeleted(true)
        self.unsubscribeOnGameDelete()
      } else if ([TableEventKind.Finished].includes(kind)) {
        const snapshot = message.payload.snapshot as Snapshot
        if (self.rootStore.game.isThreeCard && snapshot.table[0].amountOfHands === THREE_CARD_AMOUNT_OF_HANDS) {
          requestAnimationFrame(async () => {
            await self.rootStore.game.getThreeCardResult()
            self.rootStore.analytics.setEvent(events.threeCard.finished, {
              chip_amount: self.rootStore.game.myPlayer?.chips
            })
            router.navigate('/game/three-card/game-over')
          })
        }
        setTimeout(() => {
          self.rootStore.game.setGame(message.payload.snapshot, kind)
          self.rootStore.game.getTournamentStats()
        }, 500)
      } else if ([TableEventKind.PlayerBuyIn].includes(kind)) {
        // check if game needs to start?
        const secondsPassed = self.rootStore.game.getSecondsPassed()
        const isGameReset = self.rootStore.game.isGameReset
        if (secondsPassed === 0 && isGameReset) {
          self.rootStore.game.startGame()
        }

        // track context in DataDog
        self.rootStore.telemetry.setGameAttributes(getGameAttributeData)
        logger.debug(getGameAttributeData)

        return event
      } else if ([TableEventKind.PlayerRemovedByTeacherEvent].includes(kind)) {
        const leavingPlayer = message.payload.snapshot?.forPlayer
        const myPlayer = self.rootStore.game.myPlayer
        if (leavingPlayer && myPlayer && leavingPlayer.id === myPlayer.id) {
          self.rootStore.game.leaveCurrentGame(false)
          self.unsubscribeOnRemovedByTeacher()
        }
        self.rootStore.game.removeRemovedPlayer(leavingPlayer.id)
      }
      return event
    }
  }))
  .actions((self) => ({
    checkGameEvents(callback?: (callback?: () => void) => void) {
      const shouldProcessGameEvent = !self.timeout
      if (shouldProcessGameEvent) {
        // process next event
        const event = self.processEvent()
        if (event) {
          if (event.message?.payload?.version !== undefined) {
            self.setLastEventVersion(event.message.payload.version)
          }
          self.eventQueue.shift()

          const teardown = () => {
            // teardown last message
            self.rootStore.game.clearAllIn()

            self.clearTimeout()
          }
          const next = () => {
            teardown()
            callback?.(callback)
          }

          if (self.eventQueue.length > 0) {
            // NOTE: infinite recursion without blowing up the callstack
            // https://dawertz.medium.com/infinite-recursion-with-javascript-5a5a27b7d7fc
            const timeout = setTimeout(async () => {
              next()
            }, 0)
            // @ts-expect-error
            self.setTimeout(timeout)
          }
        }
      }
    }
  }))
  .actions((self) => ({
    pushGameEvents(data: GameEventType[]) {
      const clone = uniqBy([...self.eventQueue, ...data], (event) => event.message.payload.id)
      const sorted = orderBy(
        clone,
        [(event) => event.message.payload.timestamp, (event) => event.message.payload.version],
        ['asc', 'asc']
      )
      self.eventQueue.replace(castToSnapshot(sorted))
      self.checkGameEvents(self.checkGameEvents)
    }
  }))
  .actions((self) => {
    const afterCreate = async () => {
      self.incomingEventQueue.clear()
      self.clearTimeout()
      self.clearGameEvents()
    }

    const beforeDestroy = () => {
      return
    }

    return { afterCreate, beforeDestroy }
  })
/**
 * The GameEventQueueStore instance.
 */
export type GameEventQueueStore = Instance<typeof GameEventQueue>

/**
 * The data of a GameEventQueueStore.
 */
export type GameEventQueueStoreSnapshot = SnapshotOut<typeof GameEventQueue>
